<script>
  /**
   * @component ActivityUserAgents
   * @description A Svelte component that parses user agent strings and displays corresponding icons
   * with tooltips for device type, operating system, and browser. Features special handling for
   * Google Image Proxy user agents.
   *
   * @prop {string | null} ua - User agent string to parse and display
   *
   * @slot default - Custom rendering slot that receives {metadata} prop with parsed agent data
   *
   * @example
   * <ActivityUserAgents ua="Mozilla/5.0 (Windows NT 10.0) Chrome/91.0" />
   *
   * @example
   * <ActivityUserAgents ua="Mozilla/5.0 (iPhone) Safari/14.1" />
   *
   * @example
   * <!-- Custom rendering with slot -->
   * <ActivityUserAgents ua={userAgent} let:metadata>
   *   {#each metadata as item}
   *     <CustomIcon icon={item.icon} tooltip={item.label} />
   *   {/each}
   * </ActivityUserAgents>
   */

  import { format } from 'svelte-i18n';
  import { Tooltip } from '@sveltestrap/sveltestrap';
  import { UAParser } from 'ua-parser-js';

  import { ACTIVITY_AGENT_ICONS } from '@client/enums';

  import { generateUUID } from '@common/utils/strings';

  /**
   * The user agent string to parse and display.
   * When null or empty, the component will still render but show unknown/default icons.
   * @type {string | null}
   */
  export let ua = null;

  /**
   * Fallback label when data is missing.
   * @type {string}
   */
  const unknown = $format('label.UNKNOWN');

  /**
   * @typedef {Object} AgentMeta
   * @property {string} id - Unique tooltip ID for this agent metadata item
   * @property {string} icon - CSS class name for the icon (e.g., 'os-icon-browser-chrome')
   * @property {string} label - Human-readable descriptive label shown in tooltip
   */

  /**
   * @typedef {Object} ParsedAgentMeta
   * @property {AgentMeta} [device] - Device information (desktop, mobile, tablet)
   * @property {AgentMeta} [os] - Operating system information (Windows, macOS, iOS, etc.)
   * @property {AgentMeta} [browser] - Browser information (Chrome, Safari, Firefox, etc.)
   * @property {AgentMeta} [proxy] - Proxy information (only present for Google proxy agents)
   */

  /**
   * Parses a user agent string into structured agent metadata.
   * If the UA indicates a Google proxy, Device/OS/Browser are replaced with proxy info.
   * Uses ua-parser-js library to extract device, OS, and browser information.
   *
   * @param {string | null} ua - The user agent string to parse
   * @returns {ParsedAgentMeta} Parsed metadata with device, os, browser, or proxy information
   *
   * @example
   * // Standard user agent
   * parseUserAgent('Mozilla/5.0 (Windows NT 10.0) Chrome/91.0')
   * // Returns: { device: {...}, os: {...}, browser: {...} }
   *
   * @example
   * // Google proxy user agent
   * parseUserAgent('Mozilla/5.0 (compatible; GoogleImageProxy)')
   * // Returns: { proxy: {...} }
   */
  const parseUserAgent = (ua) => {
    const id = generateUUID();
    const { device, os, browser } = UAParser(ua);
    const { vendor = '', model = '', type } = device;

    // Build configuration object with device, OS, and browser metadata
    const config = {
      device: {
        icon: ACTIVITY_AGENT_ICONS[device.type] || 'os-icon-monitor',
        label: vendor ? `${vendor} ${model}` : type === 'mobile' ? $format('label.MOBILE') : $format('label.DESKTOP'),
        id: `tooltip-device-${id}`
      },
      os: {
        id: `tooltip-os-${id}`,
        icon: ACTIVITY_AGENT_ICONS[os.name],
        label: os?.name ? `${os.name} ${os.version}` : unknown
      },
      browser: {
        id: `tooltip-browser-${id}`,
        icon: ACTIVITY_AGENT_ICONS[browser.name],
        label: browser?.name ? `${browser.name} ${browser.version}` : unknown
      }
    };

    // Special handling for Google Image Proxy user agents
    if (ua?.indexOf('GoogleImageProxy') > -1) {
      config.proxy = {
        icon: 'os-icon-google',
        label: 'Google Proxy',
        id: `tooltip-device-${id}`
      };

      // Remove standard metadata when proxy is detected
      delete config.device;
      delete config.os;
      delete config.browser;
    }

    return config;
  };

  /**
   * Reactive array of agent metadata properties, automatically updates when ua prop changes.
   * Converts the parsed agent metadata object into an array for easier iteration in the template.
   * @type {AgentMeta[]}
   */
  $: metadata = Object.values(parseUserAgent(ua));
</script>

<span class="user-agent">
  <slot {metadata}>
    {#each metadata as item}
      {#if item.icon}
        <i id={item.id} class={item.icon} />
        <Tooltip target={item.id}>
          {item.label}
        </Tooltip>
      {/if}
    {/each}
  </slot>
</span>

<style lang="scss" src="./activity-user-agents.scss"></style>
